import React, { useState, useEffect } from 'react';

/**
 * Modal para descalificar a un participante de 'Memorama Individual' (Tile 51)
 * con registro obligatorio del motivo de auditoría.
 *
 * @component
 * @param {Object} props
 * @param {boolean} props.isOpen - Visibilidad del modal.
 * @param {Object} props.participant - Aficionado seleccionado { id, name, email, phone, ... }.
 * @param {Function} props.onClose - Callback al cerrar o cancelar.
 * @param {Function} props.onConfirm - Callback al confirmar la descalificación, recibe (reason).
 * @param {boolean} [props.isLoading=false] - Indicador de operación en proceso.
 */
export function MemoryIndividualDisqualifyModal({
    isOpen,
    participant,
    onClose,
    onConfirm,
    isLoading = false
}) {
    const [reason, setReason] = useState('');

    useEffect(() => {
        if (isOpen) {
            setReason('');
        }
    }, [isOpen, participant]);

    if (!isOpen || !participant) return null;

    const trimmedReason = reason.trim();
    const isValid = trimmedReason.length >= 5;

    const handleSubmit = (e) => {
        e.preventDefault();
        if (!isValid || isLoading) return;
        if (onConfirm) {
            onConfirm(trimmedReason);
        }
    };

    return (
        <div
            className="modal fade show d-block"
            tabIndex="-1"
            role="dialog"
            aria-modal="true"
            aria-labelledby="memoryDisqualifyModalTitle"
            style={{
                backgroundColor: 'rgba(15, 23, 42, 0.7)',
                backdropFilter: 'blur(4px)',
                zIndex: 1070
            }}
            onClick={(e) => {
                if (e.target === e.currentTarget && !isLoading) onClose();
            }}
        >
            <div className="modal-dialog modal-md modal-dialog-centered">
                <div className="modal-content shadow-lg border-0 rounded-4 overflow-hidden">
                    <div className="modal-header bg-danger text-white border-0 px-4 py-3">
                        <h5 id="memoryDisqualifyModalTitle" className="modal-title fw-bold d-flex align-items-center gap-2 mb-0">
                            <i className="bi bi-shield-x fs-5"></i>
                            <span>Descalificar Aficionado</span>
                        </h5>
                        <button
                            type="button"
                            className="btn-close btn-close-white"
                            aria-label="Cerrar"
                            disabled={isLoading}
                            onClick={onClose}
                        ></button>
                    </div>

                    <div className="modal-body p-4 bg-light">
                        {/* Resumen del Aficionado */}
                        <div className="card border-0 shadow-sm p-3 bg-white rounded-3 mb-3">
                            <div className="d-flex align-items-center justify-content-between mb-2">
                                <span className="fw-bold text-dark fs-6">{participant.name}</span>
                                {participant.rank > 0 && (
                                    <span className="badge bg-primary-subtle text-primary fw-bold font-monospace px-2.5 py-1">
                                        Posición #{participant.rank}
                                    </span>
                                )}
                            </div>
                            <div className="small text-secondary mb-1 d-flex align-items-center gap-2">
                                <i className="bi bi-envelope text-muted"></i>
                                <span>{participant.email}</span>
                            </div>
                            {participant.phone && (
                                <div className="small text-secondary d-flex align-items-center gap-2">
                                    <i className="bi bi-telephone text-muted"></i>
                                    <span>{participant.phone}</span>
                                </div>
                            )}
                        </div>

                        {/* Formulario de captura de motivo */}
                        <form onSubmit={handleSubmit} id="memoryDisqualifyForm">
                            <label htmlFor="memoryDisqualifyReason" className="form-label fw-semibold text-dark small mb-1">
                                Motivo de descalificación <span className="text-danger">*</span>
                            </label>
                            <textarea
                                id="memoryDisqualifyReason"
                                className={`form-control mb-1 ${reason.length > 0 && !isValid ? 'is-invalid' : ''}`}
                                rows="3"
                                placeholder="Ej: Registro duplicado / Tiempo inconsistente con telemetría / Incumplimiento de bases..."
                                value={reason}
                                onChange={(e) => setReason(e.target.value)}
                                disabled={isLoading}
                                required
                                autoFocus
                            ></textarea>
                            {reason.length > 0 && !isValid && (
                                <div className="text-danger small mb-2" style={{ fontSize: '0.8rem' }}>
                                    El motivo debe tener al menos 5 caracteres ({trimmedReason.length}/5).
                                </div>
                            )}
                            <small className="text-muted d-block mt-2" style={{ fontSize: '0.78rem', lineHeight: '1.4' }}>
                                Este motivo quedará registrado en la bitácora de auditoría y en la exportación oficial de Excel. Si es necesario, podrás restituir al aficionado más adelante desde el padrón.
                            </small>
                        </form>
                    </div>

                    <div className="modal-footer bg-white border-top px-4 py-3 d-flex justify-content-between">
                        <button
                            type="button"
                            className="btn btn-outline-secondary px-4 fw-semibold"
                            onClick={onClose}
                            disabled={isLoading}
                        >
                            Cancelar
                        </button>
                        <button
                            type="submit"
                            form="memoryDisqualifyForm"
                            className="btn btn-danger px-4 fw-bold d-flex align-items-center gap-2"
                            disabled={isLoading || !isValid}
                        >
                            {isLoading ? (
                                <>
                                    <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                                    <span>Descalificando...</span>
                                </>
                            ) : (
                                <>
                                    <i className="bi bi-slash-circle"></i>
                                    <span>Confirmar Descalificación</span>
                                </>
                            )}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default MemoryIndividualDisqualifyModal;
